import { actions } from "@app/utils";
import { createApiClient } from "@app/utils/apiClient";

const MAX_CONCURRENT_REQUESTS = 50;

export const ccLibrariesClient = createApiClient(
  actions["exd-storyteller-ai/ccLibrariesProxy"],
  MAX_CONCURRENT_REQUESTS,
);

/**
 * Fetches Creative Cloud Libraries based on the provided parameters.
 *
 * @param {object} ims - The IMS object containing user information.
 * @param {object} params - The request parameters for the CC Libraries API.
 *
 * @returns {Promise<Object>} - A promise that resolves with the CC Libraries response.
 */
export const getElements = async (data = {}, options = {}) => {
  const response = await ccLibrariesClient.post(
    "",
    {
      baseUrl: "https://platform-cs-edge.adobe.io",
      method: "GET",
      path: `/api/v1/libraries/${data.libraryId}/elements`,
      queryParams: {
        selector: data.selector ?? "details",
        limit: data.limit ?? 10,
        start: data.start ?? 0,
        orderBy: data.orderBy ?? "-modified_date",
      },
    },
    options,
  );

  if (!response.ok) {
    const errorMsg = `Failed to fetch brand libraries for libraryId: ${data.libraryId}. Status: ${response.status}`;
    console.error(errorMsg, response);
    throw new Error(errorMsg, { cause: response });
  }

  return response?.data?.data;
};

export const getLibrary = async (data = {}) => {
  const response = await ccLibrariesClient.post("", {
    baseUrl: "https://platform-cs-edge.adobe.io",
    method: "GET",
    path: `/api/v1/libraries/${data.libraryId}`,
    queryParams: {
      selector: data.selector ?? "details",
      limit: data.limit ?? 10,
      start: data.start ?? 0,
      orderBy: data.orderBy ?? "-modified_date",
    },
  });

  if (!response.ok) {
    throw new Error("Failed to fetch brand libraries", response);
  }

  return response?.data?.data;
};

/**
 * Fetches brand libraries from Creative Cloud Libraries.
 *
 * @returns {Promise<Object>} - A promise that resolves with the brand libraries response.
 */
export const getBrands = async () => {
  const response = await ccLibrariesClient.post("", {
    baseUrl: "https://libraries.adobe.io",
    method: "POST",
    path: "/api/v1/search",
    data: {
      limit: 25,
      start: 0,
      orderBy: "-modified_date",
      owner: "private,incoming,outgoing",
      selector: "full",
      library_roles_type: ["brand"],
      asset_type: ["library"],
    },
  });

  if (!response.ok) {
    throw new Error("Failed to fetch brand libraries", response);
  }

  return response?.data?.data?.libraries ?? [];
};
